'use client'

import classNames from 'clsx'
import { AnimatePresence, m } from 'motion/react'
import { useLocale } from 'next-intl'
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type WheelEvent
} from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg'
import useClickOutside from '@/hooks/useClickOutside'
import { usePathname, useRouter } from '@/i18n/routing'
import { preserveWindowScroll } from '@/lib/navigation/preserveWindowScroll'
import { scheduleDomMeasure } from '@/utils/scheduleDomMeasure'

import S from './LangSelector.module.scss'
import { LOCALE_COUNTRY_FLAGS } from './localeCountryFlags'

const CountryFlag = ({
  countryCode,
  className
}: {
  countryCode: string
  className?: string
}) => {
  const Component = LOCALE_COUNTRY_FLAGS[countryCode]
  if (!Component) return null
  return <Component className={className} title={countryCode} />
}

const LOCALES = [
  { code: 'en', name: 'English', country: 'GB' },
  { code: 'ru', name: 'Русский', country: 'RU' },
  // { code: 'ar', name: 'العربية', country: 'SA' }, // temporarily disabled
  { code: 'de', name: 'Deutsch', country: 'DE' },
  { code: 'es', name: 'Español', country: 'ES' },
  // { code: 'fa', name: 'فارسی', country: 'IR' }, // temporarily disabled
  { code: 'fr', name: 'Français', country: 'FR' },
  // { code: 'hi', name: 'हिन्दी', country: 'IN' }, // temporarily disabled
  { code: 'hu', name: 'Magyar', country: 'HU' },
  { code: 'it', name: 'Italiano', country: 'IT' },
  { code: 'ja', name: '日本語', country: 'JP' },
  { code: 'pt', name: 'Português', country: 'PT' }
  // { code: 'tr', name: 'Türkçe', country: 'TR' } // temporarily disabled
] as const

type LocaleEntry = (typeof LOCALES)[number]

export type LangSelectorLayout = 'default' | 'mobileBlock'

const mobileListExpandTransition = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1] as const
}

const MOBILE_LIST_EXPAND_MS = Math.round(
  mobileListExpandTransition.duration * 1000
)

/** Row label in mobile list (Figma uses 日本 for Japanese). */
function listLabel(lang: LocaleEntry): string {
  if (lang.code === 'ja') {
    return '日本'
  }
  return lang.code.toUpperCase()
}

interface LangSelectorProps {
  layout?: LangSelectorLayout
}

function absorbWheelInScrollable(e: WheelEvent<HTMLDivElement>) {
  const el = e.currentTarget
  const { scrollTop, scrollHeight, clientHeight } = el
  if (scrollHeight <= clientHeight + 1) {
    return
  }
  const dy = e.deltaY
  const roomAbove = scrollTop > 0
  const roomBelow = scrollTop + clientHeight < scrollHeight - 1
  if ((dy < 0 && roomAbove) || (dy > 0 && roomBelow)) {
    e.stopPropagation()
    return
  }
  e.stopPropagation()
}

const LangSelector = ({ layout = 'default' }: LangSelectorProps) => {
  const VIEWPORT_PADDING = 12
  const DROPDOWN_OFFSET = 8

  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>()
  const ref = useClickOutside<HTMLDivElement>(isOpen, () => setIsOpen(false))

  const currentLocale = useMemo(
    () => LOCALES.find(lang => lang.code === locale),
    [locale]
  )

  const otherLocales = useMemo(
    () => LOCALES.filter(lang => lang.code !== locale),
    [locale]
  )

  const toggle = () => setIsOpen(prev => !prev)

  /** Mobile drawer: after list expands, scroll so the bottom of the block is in view. */
  useLayoutEffect(() => {
    if (!isOpen || layout !== 'mobileBlock') {
      return
    }
    const root = ref.current
    if (!root) {
      return
    }
    const scrollBlockEnd = () => {
      root.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
        inline: 'nearest'
      })
    }
    const raf = requestAnimationFrame(scrollBlockEnd)
    const afterExpand = window.setTimeout(
      scrollBlockEnd,
      MOBILE_LIST_EXPAND_MS + 40
    )
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(afterExpand)
    }
  }, [isOpen, layout])

  useEffect(() => {
    if (!isOpen || layout === 'mobileBlock') {
      return
    }

    const updateDropdownPlacement = () => {
      const wrapper = ref.current
      if (!wrapper) return

      const rect = wrapper.getBoundingClientRect()
      const viewportCandidates = [
        window.visualViewport?.height,
        window.innerHeight,
        document.documentElement.clientHeight
      ].filter(
        (value): value is number => typeof value === 'number' && value > 0
      )
      const viewportHeight = Math.min(...viewportCandidates)

      const spaceBelow =
        viewportHeight - rect.bottom - VIEWPORT_PADDING - DROPDOWN_OFFSET
      const availableSpace = Math.max(0, Math.floor(spaceBelow))
      setDropdownMaxHeight(availableSpace)
    }

    let cancelScheduledPlacement = scheduleDomMeasure(updateDropdownPlacement)

    const handleResize = () => {
      cancelScheduledPlacement()
      cancelScheduledPlacement = scheduleDomMeasure(updateDropdownPlacement)
    }

    window.addEventListener('resize', handleResize, { passive: true })
    window.addEventListener('orientationchange', handleResize, {
      passive: true
    })
    window.visualViewport?.addEventListener('resize', handleResize)

    return () => {
      cancelScheduledPlacement()
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      window.visualViewport?.removeEventListener('resize', handleResize)
    }
  }, [isOpen, layout])

  const handleSelect = (code: string) => {
    // OCE-2533: only the live URL is authoritative. Profile tabs may update
    // the query via history.replaceState, while Next's useSearchParams stays
    // stale — falling back to it re-applied ?tab=arena-points on Overview.
    const query = window.location.search.replace(/^\?/, '')
    const href = query ? `${pathname}?${query}` : pathname

    // Keep ?tab=… across locale swaps. Soft-nav + refresh can still bump
    // scroll — pin it briefly, and release on the first user scroll gesture.
    const releaseScroll = preserveWindowScroll(() => {
      router.replace(href, { locale: code, scroll: false })
      router.refresh()
    }, 800)
    setIsOpen(false)
    window.setTimeout(releaseScroll, 850)
  }

  if (layout === 'mobileBlock') {
    return (
      <div className={classNames(S.wrapper, S.wrapperMobileBlock)} ref={ref}>
        <button
          type="button"
          className={S.triggerMobileBlock}
          onClick={toggle}
          aria-expanded={isOpen}
        >
          <span className={S.triggerLabel}>
            {currentLocale && (
              <CountryFlag
                countryCode={currentLocale.country}
                className={S.flagMobileBlock}
              />
            )}
            <span className={S.triggerCode}>
              {currentLocale ? listLabel(currentLocale) : ''}
            </span>
          </span>
          <ChevronDownIcon
            className={classNames(
              S.chevronMobile,
              isOpen && S.chevronMobileOpen
            )}
            aria-hidden
          />
        </button>

        <AnimatePresence initial={false}>
          {isOpen && (
            <m.div
              key="lang-mobile-dropdown"
              className={S.mobileBlockListMotion}
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              transition={mobileListExpandTransition}
              style={{ overflow: 'hidden' }}
            >
              <div className={S.mobileBlockList}>
                <div
                  className={S.mobileBlockScroll}
                  data-testid="lang-mobile-scroll"
                  onWheel={absorbWheelInScrollable}
                >
                  {otherLocales.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      className={S.mobileBlockOption}
                      onClick={() => handleSelect(lang.code)}
                    >
                      <CountryFlag
                        countryCode={lang.country}
                        className={S.flagMobileBlock}
                      />
                      <span className={S.triggerCode}>{listLabel(lang)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div className={S.wrapper} ref={ref}>
      <button
        className={S.selectorButton}
        onClick={toggle}
        aria-expanded={isOpen}
        type="button"
      >
        <div className={S.selectorContent}>
          {currentLocale && (
            <CountryFlag
              countryCode={currentLocale.country}
              className={S.flag}
            />
          )}
          <span className={S.code}>
            {(currentLocale?.code || '').toUpperCase()}
          </span>
        </div>
      </button>

      {isOpen && (
        <div
          className={S.dropdownMenu}
          onWheel={absorbWheelInScrollable}
          style={{
            maxHeight:
              typeof dropdownMaxHeight === 'number'
                ? `${dropdownMaxHeight}px`
                : undefined
          }}
        >
          {otherLocales.map(lang => (
            <button
              key={lang.code}
              className={S.dropdownItem}
              onClick={() => handleSelect(lang.code)}
              type="button"
            >
              <CountryFlag countryCode={lang.country} className={S.flag} />
              <span className={S.name}>{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LangSelector
